// TTS WebSocket 封装
// 说明：
// - 网关地址：/fzd-ceplatform-rag/ws/tts（已在 vue.config.js 里通过 devServer.proxy 代理到 app4）
// - 本文件封装了文档中的流程：connected -> start -> [text...] -> finish -> audio_chunk/complete
// - 支持复用连接：创建对话后建立连接，然后可以发送多个文本片段

// ✅ 布尔开关：true 使用 app4.fenzhidao.com，false 使用原逻辑（dev 走 localhost 代理，prod 走 dynamicHost/ceplatform2）
const USE_APP4_TTS_HOST = false;

/**
 * 构造 WebSocket 地址（优先使用从安卓获取的动态 host）
 * @param {string} token - 可选的 token，会设置到 cookie 中，由代理层读取并添加到请求头
 */
function buildTtsWsUrl(token = null) {
  if (typeof window === "undefined") return "";
  
  const { protocol } = window.location;
  const wsProtocol = protocol === "https:" ? "wss:" : "ws:";
  
  // 如果提供了 token，设置到 cookie 中，由代理层读取并添加到请求头
  if (token) {
    const cookieName = 'tts_token';
    const cookieValue = encodeURIComponent(token);
    document.cookie = `${cookieName}=${cookieValue}; path=/; max-age=3600; SameSite=Lax`;
  }
  
  let hostname;
  if (USE_APP4_TTS_HOST) {
    // app4 模式：统一使用 app4.fenzhidao.com（ws://app4.fenzhidao.com/fzd-ceplatform-rag/ws/tts）
    const dynamicHost = localStorage.getItem('dynamicHost');
    hostname = dynamicHost && dynamicHost.trim() !== '' ? dynamicHost.trim() : "app4.fenzhidao.com";
  } else {
    // 原逻辑：dev 用 localhost 代理，prod 用 dynamicHost/ceplatform2
    if (process.env.NODE_ENV === "development") {
      hostname = window.location.host;
    } else {
      const dynamicHost = localStorage.getItem('dynamicHost');
      hostname = dynamicHost && dynamicHost.trim() !== '' ? dynamicHost.trim() : "ceplatform2.fenzhidao.com";
    }
  }
  
  // 如果 host 已经包含协议，需要提取纯 hostname
  if (hostname.startsWith('http://')) {
    hostname = hostname.replace('http://', '');
  } else if (hostname.startsWith('https://')) {
    hostname = hostname.replace('https://', '');
  }
  
  return `${wsProtocol}//${hostname}/fzd-ceplatform-rag/ws/tts`;
}

/**
 * TTS WebSocket 连接管理器（支持复用连接，发送多个文本片段）
 */
class TtsWebSocketManager {
  constructor({ userId = "anonymous", token, onAudioChunk, onRawMessage, onError, onClose, onConnected, lastTtsSessionIdFromStorage, lastFinishedTtsSessionIdFromStorage, onTtsSessionIdUpdate, onFinishSent, shouldFinishOldTtsSession = false }) {
    this.userId = userId;
    this.token = token;
    this.onAudioChunk = onAudioChunk;
    this.onRawMessage = onRawMessage;
    this.onError = onError;
    this.onClose = onClose;
    this.onConnected = onConnected;
    this.lastTtsSessionIdFromStorage = lastTtsSessionIdFromStorage ?? null;
    this.lastFinishedTtsSessionIdFromStorage = lastFinishedTtsSessionIdFromStorage ?? null;
    this.onTtsSessionIdUpdate = onTtsSessionIdUpdate;
    this.onFinishSent = onFinishSent ?? null;
    this.shouldFinishOldTtsSession = shouldFinishOldTtsSession;

    this.ws = null;
    this.url = buildTtsWsUrl(token);
    this.isConnected = false;
    this.isSessionStarted = false;
    this.pendingTexts = []; // 等待发送的文本队列
    this.hasSentFinish = false;
    this.finishResponseHandler = null; // finish 响应监听器
    this.pendingConnectResolve = null; // 等待重新 start 的 resolve 回调
    this.pendingConnectReject = null; // 等待重新 start 的 reject 回调
    this.ttsSessionId = null; // 从 session_started 消息中获取的 ttsSessionId
    
    // 心跳和重连相关
    this.heartbeatTimer = null; // 心跳定时器
    this.heartbeatInterval = 30000; // 心跳间隔：30秒
    this.pongTimeout = null; // pong 超时定时器
    this.pongTimeoutDuration = 10000; // pong 超时时间：10秒
    this.lastPongTime = null; // 最后一次收到 pong 的时间
    this.reconnectTimer = null; // 重连定时器
    this.reconnectAttempts = 0; // 重连尝试次数
    this.maxReconnectAttempts = 10; // 最大重连次数
    this.reconnectDelay = 1000; // 初始重连延迟：1秒
    this.maxReconnectDelay = 30000; // 最大重连延迟：30秒
    this.shouldReconnect = true; // 是否应该自动重连（手动关闭时为 false）
    this.isReconnecting = false; // 是否正在重连
    this.connectPromise = null; // 当前连接 Promise（避免并发连接）
  }

  /**
   * 建立连接并发送 start
   * @param {boolean} [shouldFinishOldSession=false] 是否在 start 前发送 finish 关闭安卓存储的旧 ttsSessionId（仅新建对话时为 true）
   * @returns {Promise<void>}
   */
  async connect(shouldFinishOldSession = false) {
    // 如果已有连接 Promise 在进行中，检查它是否还有效
    if (this.connectPromise) {
      // 检查 WebSocket 状态和会话状态
      // 如果 WebSocket 已连接且会话已开始，说明之前的 Promise 已经成功，直接返回
      if (this.ws && this.ws.readyState === WebSocket.OPEN && this.isSessionStarted) {
        // 清理可能残留的 Promise（虽然已经成功）
        this.connectPromise = null;
        this.pendingConnectResolve = null;
        this.pendingConnectReject = null;
        return Promise.resolve();
      }
      
      // 如果 WebSocket 已连接但会话未开始，且 Promise 还在，说明正在等待 session_started
      // 检查是否有有效的 pendingConnectResolve（说明 Promise 还在等待中）
      if (this.ws && this.ws.readyState === WebSocket.OPEN && !this.isSessionStarted) {
        if (this.pendingConnectResolve) {
          // 有有效的 pendingConnectResolve，说明 Promise 还在等待中，继续等待
          // 使用 Promise.race 添加额外的超时保护（最多等待 6 秒）
          const timeoutPromise = new Promise((_, reject) => {
            setTimeout(() => {
              reject(new Error("等待之前的 connectPromise 超时（6秒）"));
            }, 6000);
          });
          try {
            await Promise.race([this.connectPromise, timeoutPromise]);
            // 再次确认会话已开始
            if (this.isSessionStarted) {
              return;
            } else {
              console.warn("⚠️ [WS-TTS] Promise 完成但 isSessionStarted 仍为 false，继续执行重新发送 start");
              // 清理 Promise，继续执行下面的逻辑重新发送 start
              this.connectPromise = null;
              this.pendingConnectResolve = null;
              this.pendingConnectReject = null;
            }
          } catch (error) {
            console.warn("⚠️ [WS-TTS] 等待之前的 connectPromise 超时或失败，清理并重新开始:", error.message);
            // 超时后清理 Promise，重新开始
            this.connectPromise = null;
            this.pendingConnectResolve = null;
            this.pendingConnectReject = null;
            // 继续执行下面的逻辑，重新发送 start
          }
        } else {
          // 没有 pendingConnectResolve，说明之前的 Promise 可能已经超时或完成，但 connectPromise 没有被清理
          console.warn("⚠️ [WS-TTS] 检测到 connectPromise 但没有 pendingConnectResolve，可能是残留的 Promise，清理并重新开始");
          this.connectPromise = null;
          this.pendingConnectResolve = null;
          this.pendingConnectReject = null;
          // 继续执行下面的逻辑，重新发送 start
        }
      } else {
        // WebSocket 未连接或状态异常，清理 Promise 并重新开始
        console.warn("⚠️ [WS-TTS] WebSocket 状态异常，清理旧的 connectPromise 并重新开始");
        this.connectPromise = null;
        this.pendingConnectResolve = null;
        this.pendingConnectReject = null;
      }
    }

    // 如果 WebSocket 已连接但会话未开始，需要重新发送 start
    if (this.ws && this.ws.readyState === WebSocket.OPEN && !this.isSessionStarted) {
      if (!this.isConnected) {
        // 等待 connected 消息（最多等待 3 秒）
        let waitCount = 0;
        const maxWaits = 30;
        while (waitCount < maxWaits && !this.isConnected) {
          await new Promise((resolve) => setTimeout(resolve, 100));
          waitCount++;
        }
        if (!this.isConnected) {
          console.warn("⚠️ [WS-TTS] 等待 connected 消息超时，但继续尝试发送 start");
        }
      }

      // 重置 finish 标志
      this.hasSentFinish = false;
      // 清空旧的 ttsSessionId（新会话会有新的 ttsSessionId）
      this.ttsSessionId = null;
      // 创建 Promise 等待 session_started
      const currentPromise = new Promise((resolve, reject) => {
        // 设置超时
        const timeout = setTimeout(() => {
          // 超时后检查是否已经收到 session_started
          if (this.isSessionStarted) {
            // 如果已经收到 session_started，直接 resolve
            // 但是可以调用 resolve，让 Promise 正常完成
            if (this.pendingConnectResolve) {
              // 如果 pendingConnectResolve 还存在，说明超时检查先于 session_started 到达
              // 这种情况不应该发生，但为了安全，先清理
              this.pendingConnectResolve = null;
              this.pendingConnectReject = null;
            }
            // 确保清理的是当前的 Promise
            if (this.connectPromise === currentPromise) {
              this.connectPromise = null;
            }
            resolve();
          } else {
            console.error("❌ [WS-TTS] 等待 session_started 超时（5秒），WebSocket 状态:", this.ws?.readyState);
            // 清理状态（确保清理的是当前的 Promise）
            if (this.connectPromise === currentPromise) {
              this.connectPromise = null;
            }
            this.pendingConnectResolve = null;
            this.pendingConnectReject = null;
            reject(new Error("等待 session_started 超时"));
          }
        }, 5000);
        
        // 保存 resolve 和 reject，在 onmessage 的 session_started 处理中调用
        // 注意：使用箭头函数保存 this 上下文，确保 timeout 可以被清理
        const savedTimeout = timeout; // 保存 timeout 引用，防止被覆盖
        this.pendingConnectResolve = () => {
          clearTimeout(savedTimeout);
          // 确保清理的是当前的 Promise
          if (this.connectPromise === currentPromise) {
            this.connectPromise = null;
          }
          this.pendingConnectResolve = null;
          this.pendingConnectReject = null;
          resolve();
        };
        this.pendingConnectReject = (err) => {
          clearTimeout(savedTimeout);
          console.error("❌ [WS-TTS] pendingConnectReject 被调用，reject connectPromise:", err);
          // 确保清理的是当前的 Promise
          if (this.connectPromise === currentPromise) {
            this.connectPromise = null;
          }
          this.pendingConnectResolve = null;
          this.pendingConnectReject = null;
          reject(err);
        };
      });
      
      this.connectPromise = currentPromise;

      try {
        if (!this.isConnected) {
          console.warn("⚠️ [WS-TTS] 仍未收到 connected 消息，但继续尝试发送 start");
        }
        
        // 先发送 finish 关闭原会话（仅新建对话且上次未 finish 时发送）
        this.sendFinishBeforeStart(shouldFinishOldSession);
        
        const startMessage = {
          type: "start",
          userId: this.userId,
        };

        // 确保 WebSocket 仍然打开
        if (!this.ws || this.ws.readyState !== WebSocket.OPEN) {
          throw new Error(`WebSocket 状态异常，无法发送 start: ${this.ws?.readyState}`);
        }
        
        this.ws.send(JSON.stringify(startMessage));
      } catch (error) {
        console.error("❌ [WS-TTS] 发送 start 消息失败:", error);
        // 发送失败，reject Promise
        if (this.pendingConnectReject) {
          this.pendingConnectReject(error);
        } else {
          // 如果没有 pendingConnectReject，说明 Promise 可能已经被清理，直接 reject
          if (this.connectPromise) {
            // 手动 reject Promise（虽然这不是最佳实践，但可以确保 Promise 被清理）
            console.warn("⚠️ [WS-TTS] 发送失败但 pendingConnectReject 不存在，清理 connectPromise");
            this.connectPromise = null;
          }
        }
        this.connectPromise = null;
        throw error;
      }
      
      return this.connectPromise;
    }

    if (this.ws && this.ws.readyState === WebSocket.OPEN && this.isSessionStarted) {
      return;
    }


    // 清理旧连接
    this.cleanup();

    // 调试用：打印 TTS WebSocket 代理路径（与 vue.config.js 中 /fzd-ceplatform-rag 的 target 保持一致）
    if (process.env.NODE_ENV === 'development') {
      const TTS_PROXY_TARGET = 'wss://ceplatform.fenzhidao.com'
      const wsPath = '/fzd-ceplatform-rag/ws/tts'
      console.log('[WS-TTS] 连接地址:', this.url)
      console.log('   -> 代理转发到:', TTS_PROXY_TARGET + wsPath, '(正式服)')
    }

    this.connectPromise = new Promise((resolve, reject) => {
      try {
        this.ws = new WebSocket(this.url);

        this.ws.onopen = () => {
          // 重置重连相关状态
          this.reconnectAttempts = 0;
          this.reconnectDelay = 1000;
          this.isReconnecting = false;
        };

        this.ws.onerror = (event) => {
          console.error("❌ [WS-TTS] WebSocket 错误:", event);
          if (typeof this.onError === "function") {
            this.onError(event);
          }
          // 错误时不立即 reject，等待 onclose 处理重连
        };

        this.ws.onclose = (event) => {
          this.isConnected = false;
          this.isSessionStarted = false;
          this.connectPromise = null;
          
          // 清理心跳
          this.stopHeartbeat();
          
          // 如果不是手动关闭，且应该重连，则自动重连
          if (this.shouldReconnect && !event.wasClean && this.reconnectAttempts < this.maxReconnectAttempts) {
            this.scheduleReconnect();
          } else {
            if (typeof this.onClose === "function") {
              this.onClose();
            }
          }
        };

        this.ws.onmessage = (event) => {
          let msg = event.data;

          if (typeof msg === "string") {
            try {
              msg = JSON.parse(msg);
            } catch (e) {
              // 非 JSON 文本，直接透传
            }
          }

          if (typeof this.onRawMessage === "function") {
            this.onRawMessage(msg);
          }

          // 处理心跳响应（pong）
          if (msg && msg.type === "pong") {
            this.handlePong();
            return;
          }

          // 处理 finish 响应（检查 type 或 finishstatus 字段）
          if (this.finishResponseHandler && (msg.type === "finish_response" || msg.finishstatus !== undefined)) {
            this.finishResponseHandler(msg);
            return;
          }

          // 文档约定的 type 流程
          if (msg && msg.type === "connected") {
            this.isConnected = true;
            // 步骤 1：先发送 finish 关闭原会话（仅新建对话且上次未 finish 时发送）
            this.sendFinishBeforeStart(this.shouldFinishOldTtsSession);
            if (!this.connectPromise || !this.pendingConnectResolve) {
              if (this.ws && this.ws.readyState === WebSocket.OPEN) {
                this.ws.send(
                  JSON.stringify({
                    type: "start",
                    userId: this.userId,
                  })
                );
              }
            }
            return;
          }

          if (msg && msg.type === "session_started") {
            this.isSessionStarted = true;
            this.hasSentFinish = false; // 重置 finish 标志
            // 保存 ttsSessionId（如果存在），并回调通知外部存储到安卓
            if (msg.ttsSessionId) {
              this.ttsSessionId = msg.ttsSessionId;
              if (typeof this.onTtsSessionIdUpdate === "function") {
                this.onTtsSessionIdUpdate(msg.ttsSessionId);
              }
            } else {
              this.ttsSessionId = null;
            }
            // 处理等待中的文本队列
            this.processPendingTexts();
            // 启动心跳
            this.startHeartbeat();
            // 调用连接成功回调
            if (typeof this.onConnected === "function") {
              this.onConnected(msg);
            }
            // 如果有等待中的 connectPromise（新连接或重新 start 的情况），resolve 它
            if (this.pendingConnectResolve) {
              this.pendingConnectResolve();
            } else if (this.connectPromise) {
              this.connectPromise = null;
            }
            return;
          }

          if (msg && msg.type === "audio_chunk") {
            // 只处理 audio_chunk 类型的消息，complete 类型的消息忽略 audioData（可能是中文状态）
            if (msg.audioData && typeof this.onAudioChunk === "function") {
              this.onAudioChunk(msg.audioData, msg);
            }
            // 收到任何消息都更新 pong 时间（表示连接正常）
            this.lastPongTime = Date.now();
            return;
          }

          if (msg && msg.type === "complete") {
            // complete 类型消息只更新 pong 时间，不处理 audioData
            this.lastPongTime = Date.now();
            return;
          }

          if (msg && msg.type === "error") {
            console.error("❌ [WS-TTS] 服务端错误:", msg);
            if (typeof this.onError === "function") {
              this.onError(new Error(msg.message || "TTS WebSocket error"));
            }
            return;
          }
        };
      } catch (e) {
        this.connectPromise = null;
        reject(e);
      }
    });

    return this.connectPromise;
  }

  /**
   * 发送文本（如果会话未开始，会加入队列等待）
   * @param {string} text
   */
  sendText(text) {
    if (!text || !text.trim()) {
      return;
    }

    if (!this.isSessionStarted) {
      this.pendingTexts.push(text);
      return;
    }

    // 会话已开始，直接发送
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      try {
        const textMessage = {
          type: "text",
          text: text.trim(),
        };
        // 如果存在 ttsSessionId，添加到消息中
        if (this.ttsSessionId) {
          textMessage.ttsSessionId = this.ttsSessionId;
        }
        this.ws.send(JSON.stringify(textMessage));
      } catch (e) {
        console.error("❌ [WS-TTS] 发送文本失败:", e);
      }
    } else {
      console.warn("⚠️ [WS-TTS] WebSocket 未连接，无法发送文本", {
        hasWs: !!this.ws,
        readyState: this.ws?.readyState,
        isSessionStarted: this.isSessionStarted
      });
    }
  }

  /**
   * 处理等待中的文本队列
   */
  processPendingTexts() {
    while (this.pendingTexts.length > 0) {
      const text = this.pendingTexts.shift();
      this.sendText(text);
    }
  }

  /**
   * 在发送 start 之前，发送 finish 关闭上一次会话（使用安卓存储的 ttsSessionId）
   * 仅当 shouldSend 且 lastTtsSessionId 未被 finish 过时发送，保证每段对话只 finish 一次
   * @param {boolean} shouldSend 是否检查并发送（仅新建对话时为 true）
   */
  sendFinishBeforeStart(shouldSend = false) {
    if (!shouldSend) return;
    const lastId = typeof this.lastTtsSessionIdFromStorage === "function"
      ? this.lastTtsSessionIdFromStorage()
      : this.lastTtsSessionIdFromStorage;
    const lastFinishedId = typeof this.lastFinishedTtsSessionIdFromStorage === "function"
      ? this.lastFinishedTtsSessionIdFromStorage()
      : this.lastFinishedTtsSessionIdFromStorage;
    if (!lastId || !this.ws || this.ws.readyState !== WebSocket.OPEN) return;
    if (lastId === lastFinishedId) return;
    try {
      const msg = { type: "finish", ttsSessionId: lastId };
      this.ws.send(JSON.stringify(msg));
      if (typeof this.onFinishSent === "function") this.onFinishSent(lastId);
    } catch (e) {
      console.warn("⚠️ [WS-TTS] 发送 finish（关闭原会话）失败:", e);
    }
  }

  /**
   * 发送 finish，结束本次会话
   * @returns {Promise<void>} 不再等待 finishstatus，直接发送 finish 并 resolve
   */
  finish() {
    return new Promise((resolve, reject) => {
      if (this.hasSentFinish) {
        console.warn("⚠️ [WS-TTS] finish 已发送，跳过重复发送");
        resolve();
        return;
      }

      if (!this.ws || this.ws.readyState !== WebSocket.OPEN || !this.isSessionStarted) {
        console.warn("⚠️ [WS-TTS] WebSocket 未就绪，无法发送 finish");
        reject(new Error("WebSocket 未就绪"));
        return;
      }

      // 直接发送 finish，不再等待 finishstatus
      const finishMessage = {
        type: "finish",
      };
      const sessionIdToFinish = this.ttsSessionId;
      if (sessionIdToFinish) {
        finishMessage.ttsSessionId = sessionIdToFinish;
      }
      this.ws.send(JSON.stringify(finishMessage));
      this.hasSentFinish = true;
      // finish 后，会话已结束，需要重新 start
      this.isSessionStarted = false;
      this.ttsSessionId = null;
      if (sessionIdToFinish && typeof this.onFinishSent === "function") {
        this.onFinishSent(sessionIdToFinish);
      }
      resolve();
    });
  }

  /**
   * 启动心跳
   */
  startHeartbeat() {
    this.stopHeartbeat();
    this.lastPongTime = Date.now();
    
    this.heartbeatTimer = setInterval(() => {
      if (this.ws && this.ws.readyState === WebSocket.OPEN) {
        // 发送心跳（ping）
        try {
          this.ws.send(
            JSON.stringify({
              type: "ping",
            })
          );

          // 设置 pong 超时检测
          this.checkPongTimeout();
        } catch (e) {
          console.error("❌ [WS-TTS] 发送心跳失败:", e);
        }
      } else {
        // 连接已断开，停止心跳
        this.stopHeartbeat();
      }
    }, this.heartbeatInterval);
  }

  /**
   * 停止心跳
   */
  stopHeartbeat() {
    if (this.heartbeatTimer) {
      clearInterval(this.heartbeatTimer);
      this.heartbeatTimer = null;
    }
    if (this.pongTimeout) {
      clearTimeout(this.pongTimeout);
      this.pongTimeout = null;
    }
  }

  /**
   * 处理 pong 响应
   */
  handlePong() {
    this.lastPongTime = Date.now();
    if (this.pongTimeout) {
      clearTimeout(this.pongTimeout);
      this.pongTimeout = null;
    }
  }

  /**
   * 检查 pong 超时
   */
  checkPongTimeout() {
    if (this.pongTimeout) {
      clearTimeout(this.pongTimeout);
    }
    
    this.pongTimeout = setTimeout(() => {
      const now = Date.now();
      const timeSinceLastPong = now - (this.lastPongTime || 0);
      
      // 如果超过超时时间没有收到 pong，认为连接已断开
      if (timeSinceLastPong > this.pongTimeoutDuration) {
        console.warn("⚠️ [WS-TTS] 心跳超时，连接可能已断开，准备重连");
        // 关闭当前连接，触发 onclose，然后重连
        if (this.ws) {
          try {
            this.ws.close();
          } catch (e) {
            // 忽略关闭错误
          }
        }
      }
    }, this.pongTimeoutDuration);
  }

  /**
   * 安排重连
   */
  scheduleReconnect() {
    if (this.isReconnecting) return;

    this.isReconnecting = true;
    this.reconnectAttempts++;

    // 计算重连延迟（指数退避）
    const delay = Math.min(
      this.reconnectDelay * Math.pow(2, this.reconnectAttempts - 1),
      this.maxReconnectDelay
    );

    this.reconnectTimer = setTimeout(async () => {
      if (!this.shouldReconnect) {
        this.isReconnecting = false;
        return;
      }

      try {
        await this.connect();
      } catch (error) {
        console.error(`❌ [WS-TTS] 第 ${this.reconnectAttempts} 次重连失败:`, error);
        
        // 如果还有重连次数，继续重连
        if (this.reconnectAttempts < this.maxReconnectAttempts) {
          this.scheduleReconnect();
        } else {
          console.error("❌ [WS-TTS] 已达到最大重连次数，停止重连");
          this.isReconnecting = false;
          if (typeof this.onError === "function") {
            this.onError(new Error("WebSocket 重连失败，已达到最大重连次数"));
          }
        }
      }
    }, delay);
  }

  /**
   * 清理资源
   */
  cleanup() {
    this.stopHeartbeat();
    
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
    // 清理 finish 响应监听器
    this.finishResponseHandler = null;
    // 清理等待重新 start 的回调
    this.pendingConnectResolve = null;
    this.pendingConnectReject = null;
    // 清空 ttsSessionId
    this.ttsSessionId = null;
  }

  /**
   * 关闭连接
   */
  close() {
    // 标记为手动关闭，不自动重连
    this.shouldReconnect = false;
    
    // 清理资源
    this.cleanup();
    
    if (this.ws) {
      try {
        this.ws.close();
      } catch (e) {
        console.error("关闭 TTS WebSocket 失败:", e);
      }
      this.ws = null;
    }
    this.isConnected = false;
    this.isSessionStarted = false;
    this.pendingTexts = [];
    this.hasSentFinish = false;
    this.isReconnecting = false;
    this.reconnectAttempts = 0;
    this.connectPromise = null;
    this.finishResponseHandler = null; // 清理 finish 响应监听器
    this.ttsSessionId = null; // 清空 ttsSessionId
  }

  /**
   * 检查连接状态
   */
  isReady() {
    return this.ws && this.ws.readyState === WebSocket.OPEN && this.isSessionStarted;
  }
}

/**
 * 创建 TTS WebSocket 管理器实例
 * @param {Object} options
 * @param {string} [options.userId] 用户标识
 * @param {function} options.onAudioChunk 收到音频时的回调
 * @param {function} [options.onRawMessage] 收到任意消息时的回调
 * @param {function} [options.onError] 错误回调
 * @param {function} [options.onClose] 关闭回调
 * @param {function} [options.onConnected] 连接成功回调（收到 session_started 时调用，参数为 session_started 消息对象）
 * @param {string|function} [options.lastTtsSessionIdFromStorage] 上次存储的 ttsSessionId（或返回它的 getter）
 * @param {string|function} [options.lastFinishedTtsSessionIdFromStorage] 已 finish 过的 ttsSessionId（或返回它的 getter），用于判断是否需补发 finish
 * @param {function} [options.onTtsSessionIdUpdate] 收到新 ttsSessionId 时的回调，用于存储到安卓
 * @param {function} [options.onFinishSent] 发送 finish 后的回调，用于保存已 finish 的 ttsSessionId
 * @param {boolean} [options.shouldFinishOldTtsSession=false] 是否在 start 前检查并补发 finish（仅新建对话时为 true）
 * @returns {TtsWebSocketManager}
 */
export function createTtsWebSocketManager(options) {
  return new TtsWebSocketManager(options);
}

/**
 * TTS WebSocket 单次文本合成（兼容旧接口，内部创建临时连接）
 * @deprecated 建议使用 createTtsWebSocketManager 复用连接
 */
export function ttsWebsocketStream({ text, userId = "anonymous", onAudioChunk, onRawMessage }) {
  const manager = createTtsWebSocketManager({ userId, onAudioChunk, onRawMessage });
  
  return manager.connect().then(() => {
    manager.sendText(text);
    manager.finish();
    // 等待 complete 后关闭（由 onclose 触发）
    return new Promise((resolve) => {
      const originalOnClose = manager.onClose;
      manager.onClose = () => {
        if (originalOnClose) originalOnClose();
        resolve();
      };
    });
  }).then(() => {
    manager.close();
  });
}


